import 'src/i18n/config';

import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';

import { MotionLazyContainer } from './components/animate';
import { initializeGA4 } from './analytics';

// React Query 클라이언트 설정
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 데이터가 5분 동안 fresh로 간주됨
      staleTime: 5 * 60 * 1000,
      // 캐시는 30분 동안 유지
      gcTime: 30 * 60 * 1000,
      // 윈도우 포커스 시 자동 refetch 비활성화 (모바일 WebView 환경 고려)
      refetchOnWindowFocus: false,
      // 네트워크 재연결 시 refetch
      refetchOnReconnect: true,
      // 실패 시 3번까지 재시도
      retry: 3,
      // 재시도 간격 (지수 백오프)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // mutation은 재시도하지 않음
      retry: false,
    },
  },
});
import { setRootStoreSingleton } from './models/root-store/root-store-singleton';
import { createBackendAdapter, setChatAdapter } from './services/chat';
// 어댑터 초기화 (실제 API 사용)
import { createBackendPrescriptionAdapter, setPrescriptionService } from './services/prescription';
import { createBackendSummaryAdapter, setSummaryService } from './services/summary';
import { RootStore, RootStoreProvider, setupRootStore } from './models';
import router from './routes';
import ThemeProvider from './theme';

function App() {
  const [rootStore, setRootStore] = useState<RootStore | undefined>(undefined);
  const { i18n } = useTranslation();

  useEffect(() => {
    initializeGA4(import.meta.env.VITE_GA4_MEASUREMENT_ID);
  }, []);

  useEffect(() => {
    console.log('[App] ===== setupRootStore 시작 =====');

    // 🆕 실제 API 어댑터 초기화 (Mock에서 Backend로 전환)
    console.log('[App] 🔧 어댑터 초기화: Mock → Backend');
    setPrescriptionService(createBackendPrescriptionAdapter());
    setSummaryService(createBackendSummaryAdapter());
    setChatAdapter(createBackendAdapter());
    console.log('[App] ✅ 어댑터 초기화 완료');

    // setup root store
    setupRootStore()
      .then(async (store) => {
        console.log('[App] ✅ setupRootStore 완료, store 생성됨:', !!store);
        setRootStore(store);
        console.log('[App] ✅ setRootStore 호출 완료');
        setRootStoreSingleton(store);
        console.log('[App] ✅ setRootStoreSingleton 호출 완료');

        // 토큰이 있을 때만 프로필 조회 (로그인 상태 복원)
        const token = store.authStore.getAccessToken();
        console.log('[App] 토큰 체크:', !!token);
        if (token) {
          console.log('[App] 토큰 있음 - getUserProfile 호출하여 로그인 상태 복원');
          // 프로필 조회로 로그인 상태 복원
          // getUserProfile 내부에서 isInitialized를 true로 설정함
          // 실패 시 (토큰 만료 등) 자동으로 리프레시 시도하거나 로그아웃 처리됨
          await store.authStore.getUserProfile();
        } else {
          console.log('[App] 토큰 없음 - isInitialized를 true로 설정');
          // 토큰이 없으면 getUserProfile을 호출하지 않으므로
          // isInitialized를 수동으로 true로 설정해야 GuestGuard가 작동함
          store.authStore.setProp('isInitialized', true);
        }
      })
      .catch((error) => {
        console.error('[App] ❌ setupRootStore 에러:', error);
      });
  }, []);

  // 언어 변경 시 locale 변경
  useEffect(() => {
    dayjs.locale(i18n.language);
  }, [i18n.language]);

  if (!rootStore) {
    console.log('[App] ⏳ rootStore가 아직 null - 로딩 중...');
    return null;
  }

  console.log('[App] ✅ rootStore 있음 - 렌더링 시작');

  return (
    <QueryClientProvider client={queryClient}>
      <RootStoreProvider value={rootStore}>
        <HelmetProvider>
          <ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MotionLazyContainer>
                <RouterProvider router={router} />
              </MotionLazyContainer>
            </LocalizationProvider>
          </ThemeProvider>
        </HelmetProvider>
      </RootStoreProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
